import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Car, Scissors, UtensilsCrossed, Lock, Mail, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Order matches the dropdown spec from the product brief.
const BUSINESS_TYPES = [
  {
    value: "parking",
    label: "Parking",
    description: "Vehicle verification for properties, lots, and HOAs.",
    icon: Car,
    available: true,
  },
  {
    value: "barbers",
    label: "Barbers",
    description: "QR check-in & loyalty for barbershops and salons.",
    icon: Scissors,
    available: false,
  },
  {
    value: "restaurants",
    label: "Restaurants",
    description: "Table QR codes, menus, and order verification.",
    icon: UtensilsCrossed,
    available: false,
  },
  {
    value: "private",
    label: "Private",
    description: "Personal or general-purpose QR use.",
    icon: Lock,
    available: false,
  },
];

export default function BusinessType() {
  const { user, setUser, properties } = useAuth();
  const nav = useNavigate();

  const [selected, setSelected] = useState("");
  const [saving, setSaving] = useState(false);
  const [comingSoon, setComingSoon] = useState(null); // null or "barbers"|"restaurants"|"private"

  // If the user already finished onboarding (has at least one property),
  // they don't need to revisit this screen — bounce them straight to the
  // dashboard. Existing users who pre-date this screen also won't have a
  // `business_type`; they're treated as "parking" implicitly because that
  // is the only flow that actually creates properties today.
  useEffect(() => {
    if (properties && properties.length > 0) {
      nav("/dashboard", { replace: true });
    }
  }, [properties, nav]);

  // If the user revisits and already chose a type previously, restore it.
  useEffect(() => {
    if (user && user.business_type) {
      setSelected(user.business_type);
      if (user.business_type !== "parking") {
        setComingSoon(user.business_type);
      }
    }
  }, [user]);

  const selectedConfig = useMemo(
    () => BUSINESS_TYPES.find((t) => t.value === selected),
    [selected]
  );

  const persist = async (value) => {
    const { data } = await api.patch("/users/me/business-type", {
      business_type: value,
    });
    if (data) setUser(data);
    return data;
  };

  const handleContinue = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await persist(selected);
      if (selected === "parking") {
        toast.success("Parking mode selected — let's set up your property.");
        nav("/onboarding", { replace: true });
      } else {
        setComingSoon(selected);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // ---------- "Coming soon" branch ----------
  if (comingSoon && comingSoon !== "parking") {
    const cfg = BUSINESS_TYPES.find((t) => t.value === comingSoon);
    const Icon = cfg?.icon || Sparkles;
    return (
      <div
        className="min-h-screen bg-[#FAFAFA] px-4 py-12 md:py-20 flex items-start justify-center"
        data-testid="business-type-coming-soon"
      >
        <div className="max-w-xl w-full">
          <div className="flex items-center gap-2 mb-8">
            <img
              src="/scanmint-logo.png"
              alt="ScanMint"
              className="h-9 w-auto select-none"
              draggable={false}
            />
          </div>

          <div className="bg-white border border-[#E4E4E7] rounded-xl p-8 md:p-10">
            <div className="w-14 h-14 rounded-xl bg-[#0F8F4F]/10 flex items-center justify-center mb-6">
              <Icon className="w-7 h-7 text-[#0F8F4F]" strokeWidth={1.75} />
            </div>
            <div className="mono-tiny text-[#0F8F4F]">Coming soon</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">
              {cfg?.label} mode is on the way
            </h1>
            <p className="mt-4 text-[#3F3F46] leading-relaxed">
              This QR setup type is coming soon. We've saved your interest in
              {" "}<b>{cfg?.label}</b> so we can let you know the moment it goes
              live. In the meantime, you can join the waitlist or reach out to
              support directly.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              <a
                href={`mailto:support@scanmint.app?subject=Waitlist%20—%20${encodeURIComponent(
                  cfg?.label || ""
                )}%20mode&body=Please%20add%20me%20to%20the%20${encodeURIComponent(
                  cfg?.label || ""
                )}%20waitlist.`}
                data-testid="business-type-waitlist"
                className="inline-flex items-center justify-center gap-2 h-12 rounded-md bg-[#0F8F4F] text-white text-sm font-medium px-4 hover:bg-[#0A6B3B] transition-colors"
              >
                <Mail className="w-4 h-4" /> Join the waitlist
              </a>
              <a
                href="mailto:support@scanmint.app"
                data-testid="business-type-contact-support"
                className="inline-flex items-center justify-center gap-2 h-12 rounded-md border border-[#E4E4E7] text-sm font-medium px-4 hover:border-[#0F8F4F] hover:text-[#0F8F4F] transition-colors"
              >
                Contact support
              </a>
            </div>

            <div className="mt-10 pt-6 border-t border-[#E4E4E7] flex flex-wrap items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => {
                  setComingSoon(null);
                  setSelected("");
                }}
                className="text-sm text-[#0F8F4F] font-medium hover:underline"
                data-testid="business-type-change"
              >
                ← Choose a different type
              </button>
              <Button
                variant="outline"
                onClick={async () => {
                  setSaving(true);
                  try {
                    await persist("parking");
                    toast.success("Switched to Parking — let's continue.");
                    nav("/onboarding", { replace: true });
                  } catch (err) {
                    toast.error(formatApiError(err));
                  } finally {
                    setSaving(false);
                  }
                }}
                disabled={saving}
                data-testid="business-type-use-parking"
                className="h-11"
              >
                Use Parking mode for now <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Default selection branch ----------
  return (
    <div
      className="min-h-screen bg-[#FAFAFA] px-4 py-10 md:py-16 flex items-start justify-center"
      data-testid="business-type-page"
    >
      <div className="max-w-xl w-full">
        <div className="flex items-center gap-2 mb-8">
          <img
            src="/scanmint-logo.png"
            alt="ScanMint"
            className="h-9 w-auto select-none"
            draggable={false}
          />
          <span className="mono-tiny text-[#71717A] ml-auto">Step 1 of 2</span>
        </div>

        <div className="bg-white border border-[#E4E4E7] rounded-xl p-6 md:p-10">
          <div className="mono-tiny text-[#0F8F4F]">Welcome to ScanMint</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">
            What type of QR business are you setting up?
          </h1>
          <p className="mt-3 text-[#71717A] text-sm md:text-base">
            Pick the mode that best matches how you'll use ScanMint. You can
            always switch later from Settings.
          </p>

          <div className="mt-8 space-y-2">
            <Label className="mono-tiny text-[#71717A]" htmlFor="business-type-select">
              Business type
            </Label>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger
                id="business-type-select"
                className="h-12"
                data-testid="business-type-select"
              >
                <SelectValue placeholder="Select a business type" />
              </SelectTrigger>
              <SelectContent>
                {BUSINESS_TYPES.map((t) => (
                  <SelectItem
                    key={t.value}
                    value={t.value}
                    data-testid={`business-type-option-${t.value}`}
                  >
                    <div className="flex items-center gap-2">
                      <t.icon className="w-4 h-4 text-[#0F8F4F]" strokeWidth={1.75} />
                      <span>{t.label}</span>
                      {!t.available && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider text-[#92400E] bg-[#FFFBEA] border border-[#FACC15]/40 px-1.5 py-0.5 rounded">
                          Soon
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedConfig && (
            <div
              className="mt-4 border border-[#E4E4E7] bg-[#FAFAFA] rounded-md p-4 flex items-start gap-3"
              data-testid="business-type-preview"
            >
              <div className="w-10 h-10 rounded-md bg-[#0F8F4F]/10 flex items-center justify-center shrink-0">
                <selectedConfig.icon className="w-5 h-5 text-[#0F8F4F]" strokeWidth={1.75} />
              </div>
              <div className="text-sm">
                <div className="font-display font-bold text-[#09090B]">
                  {selectedConfig.label}
                </div>
                <div className="text-[#71717A] mt-0.5">
                  {selectedConfig.description}
                </div>
                {!selectedConfig.available && (
                  <div className="mt-2 text-xs text-[#92400E]">
                    This QR setup type is coming soon — you'll be able to join
                    the waitlist next.
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="mt-8">
            <Button
              onClick={handleContinue}
              disabled={!selected || saving}
              className="h-12 w-full bg-[#0F8F4F] hover:bg-[#0A6B3B]"
              data-testid="business-type-continue"
            >
              {saving ? "Saving…" : "Continue"} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
